/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment } from 'react';

import { jsx } from '@compiled/react';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import type { IconProps, UNSAFE_NewIconProps } from '@atlaskit/icon';
import AddIcon from '@atlaskit/icon/core/add';
import ChatIcon from '@atlaskit/icon/core/ai-chat';
import SearchIcon from '@atlaskit/icon/core/search';
import MenuIcon from '@atlaskit/icon/core/sidebar-expand';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import type { LogoProps } from '@atlaskit/logo';
import { Anchor, Inline, Pressable, Text } from '@atlaskit/primitives';
import { Hide, Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import AppSwitcherIcon from './app-switcher-icon';

const styles = cssMap({
	imageContainer: {
		display: 'flex',
		alignItems: 'center',
		height: '32px',
		borderRadius: token('border.radius.100'),
	},
	commonActionsContainer: {
		display: 'flex',
		gap: token('space.100'),
		justifyContent: 'center',
		width: '100%',
		gridColumn: 2,
	},
	searchButtonRoot: {
		gridTemplateColumns: 'auto 1fr auto',
		alignItems: 'center',
		background: token('color.background.input'),
		borderRadius: token('border.radius.100'),
		boxSizing: 'border-box',
		cursor: 'text',
		display: 'none',
		height: '32px',
		maxWidth: '680px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.075'),
		width: '100%',
		'&:hover': {
			background: token('color.background.input.hovered'),
		},
		'&:active': {
			background: token('color.background.input.pressed'),
		},
		'@media (min-width: 48rem)': {
			display: 'grid',
		},
	},
	searchButtonText: {
		gridColumn: '1 / -1',
		gridRow: 1,
	},
	searchIconBefore: {
		display: 'flex',
		gridColumn: 1,
		gridRow: 1,
	},
	searchElemAfter: {
		display: 'flex',
		gridColumn: 3,
		gridRow: 1,
	},
	mobileUserActions: {
		background: 'transparent',
	},
	userActions: {
		alignItems: 'center',
		display: 'flex',
		gap: token('space.050'),
		gridColumn: 3,
	},
	homeActions: {
		alignItems: 'center',
		display: 'flex',
		gap: token('space.050'),
		gridColumn: 1,
	},
});

export const NavLogo = ({
	href,
	logo,
	mobileLogo,
	onClick,
	label,
}: {
	label?: string;
	href: string;
	logo: React.ReactElement<LogoProps>;
	mobileLogo: React.ReactElement<LogoProps>;
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) => (
	<Anchor aria-label={label} href={href} xcss={styles.imageContainer} onClick={onClick}>
		<Show below="sm">
			<Inline alignBlock="center">{mobileLogo}</Inline>
		</Show>
		<Hide below="sm">
			<Inline alignBlock="center">{logo}</Inline>
		</Hide>
	</Anchor>
);

type AppSwitcherProps = {
	label: string;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export const AppSwitcher = ({ label = 'App switcher', onClick }: AppSwitcherProps) => (
	<Hide below="sm">
		<IconButton icon={AppSwitcherIcon} label={label} appearance="subtle" onClick={onClick} />
	</Hide>
);

type MobileHamburgerProps = {
	label: string;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export const MobileHamburger = ({ label, onClick }: MobileHamburgerProps) => (
	<Show below="sm">
		<IconButton icon={MenuIcon} label={label} appearance="subtle" onClick={onClick} />
	</Show>
);

export const HomeActions = ({ children }: { children: React.ReactNode }) => (
	<div css={styles.homeActions}>{children}</div>
);

export const Search = ({
	label,
	onClick,
	iconBefore: IconBefore = SearchIcon,
	elemAfter,
}: {
	label: string;
	iconBefore?: React.ComponentType<UNSAFE_NewIconProps>;
	elemAfter?: React.ReactNode;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
}) => (
	<Fragment>
		<Pressable
			style={{
				// To win the specificity war against Emotion we move this into inline styles
				// When Emotion has been stripped from the Design System move this to Compiled.
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				cursor: 'text',
				border: `${token('border.width')} solid ${token('color.border.input')}`,
			}}
			onClick={onClick}
			xcss={styles.searchButtonRoot}
		>
			<span css={styles.searchIconBefore}>
				<IconBefore color={token('color.icon.subtle')} spacing="spacious" label="" />
			</span>
			<div css={styles.searchButtonText}>
				<Text color="color.text.subtlest">{label}</Text>
			</div>
			{elemAfter && <span css={styles.searchElemAfter}>{elemAfter}</span>}
		</Pressable>
		<Show below="xs">
			<IconButton label={label} appearance="subtle" icon={SearchIcon} onClick={onClick} />
		</Show>
	</Fragment>
);

type CreateButtonProps = {
	children: React.ReactNode;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export const CreateButton = ({ children, onClick }: CreateButtonProps) => (
	<Button appearance="primary" iconBefore={AddIcon} onClick={onClick}>
		{children}
	</Button>
);

type CommonActionsProps = {
	children: React.ReactNode;
};

export const CommonActions = ({ children }: CommonActionsProps) => (
	<div css={styles.commonActionsContainer}>{children}</div>
);

type ChatButtonProps = {
	children: React.ReactNode;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	isSelected?: boolean;
};

export const ChatButton = ({ children, onClick, isSelected }: ChatButtonProps) => (
	<Hide below="sm">
		<Button appearance="default" iconBefore={ChatIcon} onClick={onClick} isSelected={isSelected}>
			{children}
		</Button>
	</Hide>
);

type UserActionProps = {
	children: React.ReactNode;
	icon: React.ComponentType<IconProps | UNSAFE_NewIconProps>;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export const UserAction = ({ children, icon, onClick }: UserActionProps) => (
	<Hide below="sm">
		<IconButton icon={icon} label={children} onClick={onClick} appearance="subtle" />
	</Hide>
);

type AvatarDropdownActionProps = UserActionProps & { children: string };

export const AvatarDropdownAction = ({
	children,
	onClick,
	icon: Icon,
}: AvatarDropdownActionProps) => (
	<Fragment>
		<UserAction icon={Icon} onClick={onClick}>
			{children}
		</UserAction>
		<Show below="sm">
			<Pressable onClick={onClick} aria-label={children} xcss={styles.mobileUserActions}>
				<Inline alignBlock="center" space="space.100">
					<Icon label="" />
					<ChevronDownIcon label="" />
				</Inline>
			</Pressable>
		</Show>
	</Fragment>
);

export const UserActions = ({
	children,
	label = 'Actions',
}: {
	children: React.ReactNode;
	label?: string;
}) => (
	<nav aria-label={label} css={styles.userActions}>
		{children}
	</nav>
);

/**
 * We are flattening the top navigation root into the page layout top bar component
 * to support the visual refresh. This isn't needed anymore and will be cleaned up soon.
 *
 * @private
 * @deprecated
 */
export const TopNavigation = ({ children }: { children: React.ReactNode }) => (
	<Fragment>{children}</Fragment>
);
